import { ReactNode, useEffect, useState, useRef, useCallback } from 'react'
import BottomNav from './BottomNav'

const PULL_THRESHOLD = 60

interface PageLayoutProps {
  children: ReactNode
  contentClassName?: string
  bgClassName?: string
  noPadding?: boolean
  header?: ReactNode
  stickyHeader?: boolean
  /** Enable pull-to-refresh gesture */
  onRefresh?: () => Promise<void>
}

/**
 * useTelegramSafeAreas - Hybrid JS-CSS approach for safe area handling
 *
 * Per SKILL.md documentation, CSS variables may not be injected correctly,
 * so we need JavaScript fallback with computed values.
 */
function useTelegramSafeAreas() {
  const [safeAreas, setSafeAreas] = useState({
    top: 0, // default: no gap outside Telegram
    bottom: 0,
  })

  useEffect(() => {
    // Use any to access extended Telegram API properties
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = window.Telegram?.WebApp as any

    const updateSafeAreas = () => {
      if (tg) {
        // Combine device safe area + Telegram content safe area
        const contentTop = tg.contentSafeAreaInset?.top || 0
        const deviceTop = tg.safeAreaInset?.top || 0
        const contentBottom = tg.contentSafeAreaInset?.bottom || 0
        const deviceBottom = tg.safeAreaInset?.bottom || 0

        const top = Math.max(contentTop + deviceTop, 0)
        const bottom = Math.max(contentBottom + deviceBottom, 0)

        setSafeAreas({ top, bottom })

        // Also inject into CSS for components that need it
        document.documentElement.style.setProperty('--safe-area-top', `${top}px`)
        document.documentElement.style.setProperty('--safe-area-bottom', `${bottom}px`)
      }
    }

    // Initial calculation
    updateSafeAreas()

    // Listen for safe area changes (orientation, keyboard, etc.)
    if (tg) {
      tg.onEvent?.('safeAreaChanged', updateSafeAreas)
      tg.onEvent?.('contentSafeAreaChanged', updateSafeAreas)
      tg.onEvent?.('viewportChanged', updateSafeAreas)
    }

    return () => {
      if (tg) {
        tg.offEvent?.('safeAreaChanged', updateSafeAreas)
        tg.offEvent?.('contentSafeAreaChanged', updateSafeAreas)
        tg.offEvent?.('viewportChanged', updateSafeAreas)
      }
    }
  }, [])

  return safeAreas
}

/**
 * PageLayout - Unified layout component for Telegram Mini App
 *
 * Uses hybrid JS-CSS approach for reliable safe area handling:
 * - JavaScript calculates combined safe areas with fallbacks
 * - Works both inside and outside Telegram
 */
export default function PageLayout({
  children,
  contentClassName = '',
  bgClassName = '',
  noPadding = false,
  header,
  stickyHeader = false,
  onRefresh,
}: PageLayoutProps) {
  const [mounted, setMounted] = useState(false)
  const safeAreas = useTelegramSafeAreas()
  const mainRef = useRef<HTMLElement>(null)

  // Pull-to-refresh state
  const [ptrState, setPtrState] = useState<'idle' | 'pulling' | 'ready' | 'refreshing'>('idle')
  const [ptrDistance, setPtrDistance] = useState(0)
  const startYRef = useRef(0)
  const isDraggingRef = useRef(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Pull-to-refresh touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const main = mainRef.current
    if (!main || !onRefresh || main.scrollTop > 0 || ptrState === 'refreshing') return
    startYRef.current = e.touches[0].clientY
    isDraggingRef.current = true
  }, [onRefresh, ptrState])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDraggingRef.current || ptrState === 'refreshing' || !onRefresh) return
    const main = mainRef.current
    if (!main || main.scrollTop > 0) return

    const distance = e.touches[0].clientY - startYRef.current
    if (distance <= 0) { setPtrDistance(0); setPtrState('idle'); return }

    const resisted = Math.min(distance * 0.5, 120)
    setPtrDistance(resisted)
    setPtrState(resisted >= PULL_THRESHOLD ? 'ready' : 'pulling')
  }, [ptrState, onRefresh])

  const handleTouchEnd = useCallback(async () => {
    isDraggingRef.current = false
    if (!onRefresh) return

    if (ptrState === 'ready') {
      setPtrState('refreshing')
      setPtrDistance(60)
      try { await onRefresh() } finally {
        setPtrState('idle')
        setPtrDistance(0)
      }
    } else {
      setPtrState('idle')
      setPtrDistance(0)
    }
  }, [ptrState, onRefresh])

  // Build dynamic styles using computed safe areas
  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    // Use computed safe area top
    paddingTop: `${safeAreas.top}px`,
    backgroundColor: 'var(--tg-theme-bg-color, #f5f5f5)',
  }

  const mainStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    // Bottom padding: bottom nav (56px) + safe area + extra breathing room
    paddingBottom: `${safeAreas.bottom + 72}px`,
  }

  if (!mounted) {
    return null // Prevent hydration mismatch
  }

  return (
    <div style={containerStyle} className={bgClassName}>
      {/* Optional header with sticky support */}
      {header && (
        <div className={stickyHeader ? 'sticky top-0 z-20' : ''}>
          {header}
        </div>
      )}

      {/* Main scrollable content */}
      <main
        ref={mainRef}
        onTouchStart={onRefresh ? handleTouchStart : undefined}
        onTouchMove={onRefresh ? handleTouchMove : undefined}
        onTouchEnd={onRefresh ? handleTouchEnd : undefined}
        style={{
          ...mainStyle,
          transform: ptrDistance > 0 ? `translateY(${ptrDistance}px)` : 'translateY(0)',
          transition: ptrState === 'refreshing' || (ptrState === 'idle' && ptrDistance === 0)
            ? 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
            : 'none',
        }}
        className={`${noPadding ? '' : 'px-4'} ${contentClassName}`}
      >
        {/* Pull indicator */}
        {onRefresh && (
          <div
            className="flex items-center justify-center overflow-hidden transition-all duration-300"
            style={{
              height: ptrDistance > 0 ? `${ptrDistance}px` : '0px',
              opacity: ptrDistance > 0 ? Math.min(ptrDistance / PULL_THRESHOLD, 1) : 0,
            }}
          >
            <div className="flex items-center gap-2 text-sm text-gray-400 select-none">
              {ptrState === 'refreshing' ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span>Refreshing...</span>
                </>
              ) : ptrState === 'ready' ? (
                <span className="text-blue-500 font-medium">Release to refresh</span>
              ) : (
                <span>Pull to refresh</span>
              )}
            </div>
          </div>
        )}
        {children}
      </main>
      {/* Bottom navigation for main pages */}
      <BottomNav />
    </div>
  )
}

/**
 * PageHeader - Glass morphism header component
 */
interface PageHeaderProps {
  children: ReactNode
  className?: string
  noBorder?: boolean
}

export function PageHeader({ children, className = '', noBorder = false }: PageHeaderProps) {
  return (
    <div
      className={`
        bg-white/80 backdrop-blur-md
        ${noBorder ? '' : 'border-b border-gray-200/50'}
        ${className}
      `}
      style={{
        backgroundColor: 'var(--tg-theme-header-bg-color, rgba(255,255,255,0.8))',
      }}
    >
      {children}
    </div>
  )
}

/**
 * PageSection - Consistent section wrapper
 */
interface PageSectionProps {
  children: ReactNode
  className?: string
  title?: string
}

export function PageSection({ children, className = '', title }: PageSectionProps) {
  return (
    <section className={`max-w-md mx-auto ${className}`}>
      {title && (
        <h2
          className="text-sm font-semibold uppercase tracking-wide mb-3"
          style={{ color: 'var(--tg-theme-section-header-text-color, #888)' }}
        >
          {title}
        </h2>
      )}
      {children}
    </section>
  )
}

/**
 * ContentCard - Card with glass morphism styling
 */
interface ContentCardProps {
  children: ReactNode
  className?: string
  onClick?: () => void
}

export function ContentCard({ children, className = '', onClick }: ContentCardProps) {
  return (
    <div
      className={`
        rounded-xl overflow-hidden
        ${onClick ? 'cursor-pointer active:scale-[0.98] transition-transform' : ''}
        ${className}
      `}
      style={{
        backgroundColor: 'var(--tg-theme-section-bg-color, #ffffff)',
        border: '1px solid var(--tg-theme-section-separator-color, rgba(0,0,0,0.1))',
      }}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

/**
 * AvatarButton - Circular avatar button for navigation
 */
interface AvatarButtonProps {
  imageUrl?: string
  initials?: string
  onClick: () => void
  size?: 'sm' | 'md'
}

export function AvatarButton({ imageUrl, initials = '?', onClick, size = 'md' }: AvatarButtonProps) {
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base'

  return (
    <button
      onClick={onClick}
      className={`${sizeClasses} rounded-full text-white font-semibold
        flex items-center justify-center overflow-hidden
        shadow-lg active:scale-95 transition-transform`}
      style={{
        background: imageUrl
          ? 'transparent'
          : `linear-gradient(135deg, var(--tg-theme-button-color, #007aff), var(--tg-theme-accent-text-color, #5856d6))`,
        border: '2px solid rgba(255,255,255,0.3)',
      }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="Profile" className="w-full h-full object-cover" />
      ) : (
        initials.charAt(0).toUpperCase()
      )}
    </button>
  )
}

/**
 * useTelegramSafeAreas hook export for external use
 */
export { useTelegramSafeAreas }
